/**
 * Header - Responsive navigation with mobile menu
 */
import { useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { Menu, Globe, LogOut } from 'lucide-react'
import { Button } from '../ui/button'
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle } from '../ui/sheet'
import { Avatar, AvatarFallback } from '../ui/avatar'
import { UserMenu } from './UserMenu'
import { ThemeToggle } from '../theme-toggle'
import { useAuth } from '@mzanzi/auth'
import { cn } from '../../lib/utils'

interface NavItem {
  label: string
  href: string
}

const publicNav: NavItem[] = [
  { label: 'Features', href: '/#features' },
  { label: 'Pricing', href: '/#pricing' },
  { label: 'About', href: '/#about' },
]

function DesktopNav({ pathname }: { pathname: string }) {
  return (
    <nav className="hidden md:flex items-center gap-6">
      {publicNav.map((item) => (
        <Link
          key={item.href}
          to={item.href}
          className={cn(
            'text-sm font-medium transition-colors hover:text-primary',
            pathname === item.href ? 'text-primary' : 'text-muted-foreground'
          )}
        >
          {item.label}
        </Link>
      ))}
    </nav>
  )
}

function DesktopActions() {
  const { isAuthenticated, user, logout } = useAuth()

  return (
    <div className="hidden md:flex items-center gap-2">
      <ThemeToggle />
      {isAuthenticated ? (
        <UserMenu name={user?.name} email={user?.email} onLogout={logout} />
      ) : (
        <>
          <Button variant="ghost" asChild>
            <Link to="/login">Sign in</Link>
          </Button>
          <Button asChild>
            <Link to="/login">Get Started</Link>
          </Button>
        </>
      )}
    </div>
  )
}

function MobileMenuContent({ pathname, onClose }: { pathname: string; onClose: () => void }) {
  const { isAuthenticated, user, logout } = useAuth()
  const initials = user?.name
    ?.split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase() || 'U'

  return (
    <>
      <nav className="flex flex-col gap-4 mt-8">
        {publicNav.map((item) => (
          <Link
            key={item.href}
            to={item.href}
            onClick={onClose}
            className={cn(
              'text-lg font-medium transition-colors hover:text-primary py-2',
              pathname === item.href ? 'text-primary' : 'text-muted-foreground'
            )}
          >
            {item.label}
          </Link>
        ))}
      </nav>

      <div className="absolute bottom-8 left-6 right-6">
        {isAuthenticated ? (
          <MobileUserSection user={user} initials={initials} onLogout={() => { logout(); onClose() }} />
        ) : (
          <div className="flex flex-col gap-3">
            <Button variant="outline" className="w-full" asChild>
              <Link to="/login" onClick={onClose}>Sign in</Link>
            </Button>
            <Button className="w-full" asChild>
              <Link to="/login" onClick={onClose}>Get Started</Link>
            </Button>
          </div>
        )}
      </div>
    </>
  )
}

function MobileUserSection({ user, initials, onLogout }: {
  user: { name?: string; email?: string } | null | undefined
  initials: string
  onLogout: () => void
}) {
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 p-3 rounded-lg bg-muted">
        <Avatar className="h-10 w-10">
          <AvatarFallback>{initials}</AvatarFallback>
        </Avatar>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium truncate">{user?.name}</p>
          <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
        </div>
      </div>
      <Button variant="outline" className="w-full gap-2" onClick={onLogout}>
        <LogOut className="h-4 w-4" />
        Sign out
      </Button>
    </div>
  )
}

export function Header() {
  const [mobileOpen, setMobileOpen] = useState(false)
  const location = useLocation()

  return (
    <header className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="container flex h-14 md:h-16 items-center justify-between px-4 md:px-6">
        <Link to="/" className="flex items-center gap-2 md:gap-3">
          <div className="flex h-8 w-8 md:h-9 md:w-9 items-center justify-center rounded-lg bg-gradient-to-br from-primary to-blue-600">
            <Globe className="h-4 w-4 md:h-5 md:w-5 text-white" />
          </div>
          <span className="text-lg md:text-xl font-bold bg-gradient-to-r from-primary to-blue-600 bg-clip-text text-transparent">
            Mzanzi
          </span>
        </Link>

        <DesktopNav pathname={location.pathname} />
        <DesktopActions />

        <div className="flex md:hidden items-center gap-2">
          <ThemeToggle />
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="h-9 w-9">
                <Menu className="h-5 w-5" />
                <span className="sr-only">Toggle menu</span>
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[300px] sm:w-[350px]">
              <SheetHeader>
                <SheetTitle className="flex items-center gap-2">
                  <Globe className="h-5 w-5 text-primary" />
                  Mzanzi Cloud
                </SheetTitle>
              </SheetHeader>
              <MobileMenuContent pathname={location.pathname} onClose={() => setMobileOpen(false)} />
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  )
}
